// Sky dome for the true-scale local scene, from the pad to high orbit.
//
// A camera-centred BackSide sphere whose fragment shader raymarches single
// scattering (Rayleigh + Mie, with ozone absorption) through a physically
// sized atmosphere. Because the local scene is in real metres, the camera's
// real altitude drives the shader directly: a blue sky on the pad, a thin
// band at 80 km, and from orbit the bright blue limb over the Earth. It is
// drawn first in the local pass with depth off and composited over the app's
// scaled scene with premultiplied alpha, so stars, the sun disc and the
// scaled planet are attenuated by the real transmittance and nothing pops
// between "surface" and "space".
//
// From orbit the dome used to fade out above about 240 km and hand over to
// the app's exterior atmosphere shell. That shell is built to be seen from
// far away; from low orbit it renders as a thick, flat, pale band over the
// horizon. The dome now stays up through high Earth orbit (looking in
// from space it samples the path uniformly, since the densest air is at the
// tangent point mid-path rather than next to the eye), and environment.js
// only brings the shell back when the atmospheric limb is nearly subpixel.

import * as THREE from 'three';
import {
  createEclipseSkyUniforms, ECLIPSE_SKY_UNIFORMS_GLSL, ECLIPSE_SKY_TOP_GLSL, ECLIPSE_SKY_SAMPLE_GLSL,
  ECLIPSE_SKY_AFTER_GLSL, ECLIPSE_SKY_DISC_GLSL,
} from '../eclipse/totality.js';

const VERT = /* glsl */`
  varying vec3 vDir;
  void main() {
    vDir = position;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    // Push to the far plane so nothing sorts behind it.
    gl_Position.z = gl_Position.w * 0.999999;
  }
`;

const FRAG = /* glsl */`
  precision highp float;
  varying vec3 vDir;
  uniform vec3 uCamPos;      // camera relative to planet centre (m)
  uniform vec3 uSunDir;
  uniform float uPlanetR;
  uniform float uAtmR;
  uniform float uCAtm;       // |cam|^2 - AtmR^2 (double-precision on CPU)
  uniform float uCGnd;       // |cam|^2 - PlanetR^2
  uniform vec3 uBetaR;
  uniform float uBetaM;
  uniform vec3 uMieAlbedo;             // aerosol single-scattering albedo per channel
  uniform float uMieIso;               // isotropic multiple-scattering share (phase units)
  uniform float uRayIso;               // the same for the air: light scattered more than once
  uniform vec3 uBetaO;       // ozone absorption at peak density (1/m)
  uniform float uHr;
  uniform float uHm;
  uniform float uG;
  uniform float uSunIntensity;
  uniform float uFade;
  uniform float uSpace;      // 0 inside the air, 1 looking in from above it
  uniform float uGroundHaze; // scale on haze over the ground seen from space
  uniform float uGroundOverGlobe; // 1: the ground is a globe with its own haze at every height (the viewer)
  uniform float uExposure;   // extra radiance scale, used by the env capture
  uniform float uSteps;      // view samples inside the air (quality tier)
  uniform float uLimbSteps;  // view samples on limb rays from space (quality tier)
  uniform float uStarDim;    // 0..1: how much a sunlit view suppresses the stars
  uniform float uAirglow;    // strength of the night airglow layer
  uniform vec3 uAirglowColor; // its colour (557.7 nm oxygen: green to yellow-green)
  uniform float uSunDisc;    // HDR radiance of the solar disc (0: no disc, as Astronomy mode draws its own)
  uniform float uSkyVeil;    // how hard a bright sky hides what is behind it (16; 0 in Astronomy mode)
  uniform float uRefract;    // bend of a ray grazing the ground from space, radians (0: none)

  const int STEPS = 32;
  const int LIGHT_STEPS = 6;

  // Rayleigh, Mie and ozone densities. Ozone is the usual tent around 25 km:
  // it barely touches the daytime sky but absorbs orange along the very long
  // twilight paths, which is what keeps the zenith blue after sunset.
  vec3 densityAt(vec3 p) {
    float h = max(0.0, length(p) - uPlanetR);
    return vec3(exp(-h / uHr), exp(-h / uHm), max(0.0, 1.0 - abs(h - 25000.0) / 15000.0));
  }

  vec3 extinction(vec3 od) {
    return uBetaR * od.x + vec3(uBetaM * 1.1 * od.y) + uBetaO * od.z;
  }

  // Optical depth from p toward the sun until the atmosphere top.
  vec3 lightDepth(vec3 p) {
    float b = dot(uSunDir, p);
    float c = dot(p, p) - uAtmR * uAtmR;
    float disc = b * b - c;
    if (disc < 0.0) return vec3(1e9);
    float tEnd = -b + sqrt(disc);
    // Planet shadow: ray toward the sun hits the ground.
    float cg = dot(p, p) - uPlanetR * uPlanetR;
    float discG = b * b - cg;
    if (b < 0.0 && discG > 0.0) return vec3(1e9);
    float ds = tEnd / float(LIGHT_STEPS);
    vec3 od = vec3(0.0);
    for (int i = 0; i < LIGHT_STEPS; i++) {
      vec3 q = p + uSunDir * (ds * (float(i) + 0.5));
      od += densityAt(q) * ds;
    }
    return od;
  }

  // The night airglow: a thin shell of oxygen emission at about 95 km that
  // shows edge-on as a green band hugging the night-side limb. Only the
  // part of the path near the tangent point matters, so it is evaluated
  // there rather than integrated.
  vec3 airglow(vec3 dir, float b) {
    if (uAirglow <= 0.0 || b >= 0.0) return vec3(0.0);
    vec3 tp = uCamPos - dir * b;                 // closest approach to the centre
    float ht = length(tp) - uPlanetR;
    float layer = exp(-pow((ht - 95000.0) / 4500.0, 2.0));
    float night = 1.0 - smoothstep(-0.2, 0.05, dot(normalize(tp), uSunDir));
    return uAirglowColor * (layer * night * uAirglow);
  }

  void main() {
    vec3 dir = normalize(vDir);
    float b = dot(dir, uCamPos);
    float disc = b * b - uCAtm;
    // Rays that miss the air entirely: space. A camera exposed for a sunlit
    // Earth or spacecraft records few stars, so a partly opaque black here
    // dims them; at night uStarDim is zero and the sky is full of them.
    if (disc <= 0.0) { gl_FragColor = vec4(0.0, 0.0, 0.0, uStarDim * uFade); return; }
    float sq = sqrt(disc);
    float t0 = max(-b - sq, 0.0);
    float t1 = -b + sq;
    bool ground = false;
    float discG = b * b - uCGnd;
    if (discG > 0.0) {
      float tg = -b - sqrt(discG);
      if (tg > 0.0) { t1 = min(t1, tg); ground = true; }
    }
    // The atmosphere may intersect the infinite ray entirely behind the
    // camera. This is the same empty space as a ray that misses the shell;
    // returning transparent here made a bright cone in the starfield.
    if (t1 <= t0) { gl_FragColor = vec4(0.0, 0.0, 0.0, uStarDim * uFade); return; }

    // Ground rays seen from space carry only part of their haze (the scaled
    // Earth already has some of its own): keep the full air column at the
    // horizon, where reducing it by 45% on every ground ray drew a hard
    // brightness step at the silhouette, and only reduce the duplicated haze
    // well inside the disc. Worked out before the march so a ray that would
    // add nothing (the viewer's globe owns all of its haze) costs nothing.
    float kGround = 1.0;
    if (ground) {
      float impact = length(cross(uCamPos, dir));
      float interior = smoothstep(0.0, uHr * 5.0, uPlanetR - impact);
      kGround = mix(1.0, uGroundHaze, max(uSpace, uGroundOverGlobe) * interior);
      if (kGround <= 0.0) { gl_FragColor = vec4(0.0); return; }
    }

    float mu = dot(dir, uSunDir);
    float phaseR = 3.0 / (16.0 * 3.14159265) * (1.0 + mu * mu);
    float g2 = uG * uG;
    float phaseM = 3.0 / (8.0 * 3.14159265) * ((1.0 - g2) * (1.0 + mu * mu)) /
                   ((2.0 + g2) * pow(1.0 + g2 - 2.0 * uG * mu, 1.5));

    // Use the same quadrature on both sides of the ground tangent. A
    // 10/32 sample switch there changed the radiance at a visible line.
    // Quadratic spacing near the ground becomes uniform above the air.
    int steps = uSpace > 0.5 ? int(uLimbSteps) : int(uSteps);
    float span = t1 - t0;
    vec3 odView = vec3(0.0);
    vec3 sumR = vec3(0.0);
    vec3 sumM = vec3(0.0);
    float prevT = t0;
    for (int i = 1; i <= STEPS; i++) {
      if (i > steps) break;
      float f = float(i) / float(steps);
      float tEnd = t0 + span * mix(f * f, f, uSpace);
      float segment = tEnd - prevT;
      float t = 0.5 * (prevT + tEnd);
      prevT = tEnd;
      vec3 p = uCamPos + dir * t;
      vec3 d = densityAt(p) * segment;
      vec3 odMid = odView + d * 0.5;
      odView += d;
      vec3 att = exp(-extinction(odMid + lightDepth(p)));
      sumR += att * d.x;
      sumM += att * d.y;
    }
    // uMieIso: light that has scattered more than once in a dusty sky,
    // spread nearly evenly over it (single scattering alone leaves a thick
    // dust sky dark away from the sun).
    vec3 color = uSunIntensity * (sumR * uBetaR * (phaseR + uRayIso) + sumM * uBetaM * uMieAlbedo * (phaseM + uMieIso));
    if (!ground) color += airglow(dir, b);
    vec3 trans = exp(-extinction(odView));
    float alpha = 1.0 - dot(trans, vec3(0.3333));
    if (ground) {
      color *= kGround;
      alpha *= kGround;
    } else {
      // From inside the air a bright sky hides what is behind it (stars, the
      // galaxy) the way an exposed camera would, whatever the physical
      // transmittance says. The zenith of a morning sky reads about 0.14 here
      // at 2 km, 0.04 at 12 km and 0.015 at 20 km: at 4x the stars showed
      // at half strength looking up from the pad; at 16x they come out above
      // about 15 km and after dusk, as they do in photographs.
      float bright = dot(color, vec3(0.2126, 0.7152, 0.0722));
      alpha = max(alpha, clamp(bright * uSkyVeil, 0.0, 1.0) * (1.0 - uSpace));
      // Exposure dims the background equally on both sides of the limb.
      // Atmospheric extinction adds to it continuously as the path grows.
      // Ground rays keep their existing haze so Earth is not darkened.
      alpha = 1.0 - (1.0 - alpha) * (1.0 - uStarDim);
      // The sun's own disc, seen through the air: the solar radiance times
      // the transmittance of this very ray, so a low sun goes orange and red
      // at dusk and through the limb at an orbital sunset, with limb
      // darkening across the disc. It covers the app's always-white sun
      // sprite behind it; the composer's bloom does the glare.
      float sunCos = 0.99998887;                          // 0.27 degree radius
      // Seen from above the air, a ray that grazes the limb at tangent
      // height h left the Sun from a direction lower by uRefract x
      // e^(-h / 8 km): the Sun near the limb shows lifted and squashed
      // flat, the more the lower it is, as in sunrise photographs from the
      // ISS.
      float muSun = mu;
      if (uRefract > 0.0 && b < 0.0) {
        vec3 tp = uCamPos - dir * b;
        float tpLen = length(tp);
        float bend = uRefract * uSpace * exp(-(tpLen - uPlanetR) / 8000.0);
        muSun = dot(normalize(dir - tp * (bend / tpLen)), uSunDir);
      }
      float edge = 1.0 - smoothstep(sunCos - 0.000004, sunCos + 0.000002, muSun);
      if (edge < 1.0 && uSunDisc > 0.0) {
        float rr = clamp((1.0 - muSun) / (1.0 - sunCos), 0.0, 1.0);
        float limb = 0.4 + 0.6 * sqrt(1.0 - rr);
        color += vec3(1.0, 0.95, 0.88) * uSunDisc * trans * limb * (1.0 - edge) / max(uExposure, 1e-3);
        alpha = max(alpha, 1.0 - edge);
      }
    }
    color *= uFade * uExposure;
    alpha *= uFade;
    gl_FragColor = vec4(color, clamp(alpha, 0.0, 1.0));
  }
`;

// Keep the physical scattering renderer through low/medium/high Earth orbit.
// Hand over only when the whole atmosphere is nearly subpixel on screen.
export const SKY_FADE_START = 30000e3;
export const SKY_FADE_END = 60000e3;

// { eclipse: true } builds the dome with a solar eclipse in it (the viewer's
// close sky, src/eclipse/totality.js): the Moon's shadow in the air and the
// eclipsed Sun drawn by the dome. Rocketry and Astronomy mode build it
// without, so their shader is unchanged.
function eclipseShader(frag) {
  // Anchored on code lines, which survive the build's GLSL minifier
  // (scripts/glsl-minify.mjs strips comments and indentation only).
  const at = [
    ['uniform float uSkyVeil;', (a) => `${a}\n${ECLIPSE_SKY_UNIFORMS_GLSL}`],
    ['void main() {', (a) => `${a}\n${ECLIPSE_SKY_TOP_GLSL}`],
    ['vec3 att = exp(-extinction(odMid + lightDepth(p)));', (a) => `${a}\n${ECLIPSE_SKY_SAMPLE_GLSL}`],
    ['vec3 color = uSunIntensity * (sumR * uBetaR * (phaseR + uRayIso) + sumM * uBetaM * uMieAlbedo * (phaseM + uMieIso));', (a) => `${a}\n${ECLIPSE_SKY_AFTER_GLSL}`],
    ['float sunCos = 0.99998887;', (a) => `${ECLIPSE_SKY_DISC_GLSL}\n${a}`],
  ];
  let out = frag;
  for (const [anchor, put] of at) {
    if (out.split(anchor).length !== 2) throw new Error(`sky.js: the dome's shader has no single "${anchor}"`);
    out = out.replace(anchor, put(anchor));
  }
  return out;
}

export class SkyDome {
  constructor({ eclipse = false } = {}) {
    this.eclipse = eclipse;
    this.uniforms = {
      uCamPos: { value: new THREE.Vector3(0, 6371000, 0) },
      uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      uPlanetR: { value: 6371000 },
      uAtmR: { value: 6371000 + 100000 },
      uCAtm: { value: 0 },
      uCGnd: { value: 0 },
      uBetaR: { value: new THREE.Vector3(5.8e-6, 13.5e-6, 33.1e-6) },
      uBetaM: { value: 16e-6 },
      uMieAlbedo: { value: new THREE.Vector3(1, 1, 1) },
      uMieIso: { value: 0 },
      uRayIso: { value: 0.05 },
      uBetaO: { value: new THREE.Vector3(0.650e-6, 1.881e-6, 0.085e-6) },
      uHr: { value: 8000 },
      uHm: { value: 1200 },
      uG: { value: 0.7 },
      // 18 blew the sunward sky and the clouds in it to white against a
      // ground exposed for the pad; 10 with a multiple-scatter share keeps
      // the zenith blue while the sun's quarter of the sky reads as sky.
      uSunIntensity: { value: 10.0 },
      uFade: { value: 1 },
      uSpace: { value: 0 },
      uGroundHaze: { value: 0.55 },
      uGroundOverGlobe: { value: 0 },
      uExposure: { value: 1 },
      uSteps: { value: 20 },
      uLimbSteps: { value: 32 },
      uStarDim: { value: 0 },
      uAirglow: { value: 0.12 },
      uAirglowColor: { value: new THREE.Vector3(0.22, 1.0, 0.32) },
      uSunDisc: { value: 60.0 },
      uSkyVeil: { value: 16.0 },
      uRefract: { value: 0 },
      ...(eclipse ? createEclipseSkyUniforms() : {})
    };
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: eclipse ? eclipseShader(FRAG) : FRAG,
      uniforms: this.uniforms,
      side: THREE.BackSide,
      depthTest: false,
      depthWrite: false,
      // Not flagged transparent on purpose: that would move the dome into
      // the transparent pass, after every opaque object, and its horizon
      // glow would paint over the vessel. Opaque list + renderOrder -100
      // draws it first; the custom blend still composites over the stars.
      transparent: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor,
      blendDstAlpha: THREE.OneMinusSrcAlphaFactor
    });
    mat.toneMapped = true;
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(1000, 48, 32), mat);
    this.mesh.renderOrder = -100;
    this.mesh.frustumCulled = false;
    this.mesh.name = 'skyDome';
    this.hasAtmosphere = true;
  }

  // Sample counts from the quality tier (settings.js); the loop's hard cap is
  // STEPS, so anything above it is clamped.
  setQuality(tier) {
    this.uniforms.uSteps.value = Math.min(32, tier.skySteps);
    this.uniforms.uLimbSteps.value = Math.min(32, tier.skyLimbSteps);
  }

  // Configure for a body definition (from bodies.js). An airless body keeps
  // the dome as a black veil over the stars alone: a camera exposed for a
  // sunlit lunar surface records no stars at all (every Apollo frame has a
  // black sky), so in daylight the stars go, and come back at night.
  setBody(def) {
    const has = !!(def && def.atmosphereHeight > 0);
    this.hasAtmosphere = has;
    this.airless = !!(def && !has && def.hasSurface);
    this.mesh.visible = has || this.airless;
    if (!has) { if (def) this.uniforms.uPlanetR.value = def.radius; return; }
    this.uniforms.uPlanetR.value = def.radius;
    this.uniforms.uAtmR.value = def.radius + (def.key === 'earth' ? 100000 : def.atmosphereHeight);
    this.uniforms.uHr.value = def.scaleHeight || 8000;
    this.uniforms.uHm.value = Math.min(1200, (def.scaleHeight || 8000) * 0.15);
    if (def.key === 'mars') {
      // Mars' sky is its dust: micron grains mixed through the whole thin
      // atmosphere to an optical depth near 0.5, scattering red and
      // absorbing blue (single-scattering albedo about 0.95 red, 0.55
      // blue), which is the butterscotch of the rover photographs; the
      // gas itself adds almost nothing.
      this.uniforms.uBetaR.value.set(19.9e-6, 13.6e-6, 5.75e-6).multiplyScalar(0.03);
      this.uniforms.uHm.value = def.scaleHeight || 11100;
      this.uniforms.uBetaM.value = 0.5 / this.uniforms.uHm.value;
      this.uniforms.uMieAlbedo.value.set(0.95, 0.74, 0.55);
      this.uniforms.uMieIso.value = 0.06;
      this.uniforms.uRayIso.value = 0;
      this.uniforms.uBetaO.value.set(0, 0, 0);
      this.uniforms.uG.value = 0.65;
    } else {
      this.uniforms.uBetaR.value.set(5.8e-6, 13.5e-6, 33.1e-6);
      this.uniforms.uBetaM.value = 16e-6;
      this.uniforms.uMieAlbedo.value.set(1, 1, 1);
      this.uniforms.uMieIso.value = 0;
      this.uniforms.uRayIso.value = 0.05;
      this.uniforms.uBetaO.value.set(0.650e-6, 1.881e-6, 0.085e-6);
      this.uniforms.uG.value = 0.7;
    }
  }

  // camPos: {x,y,z} camera relative to planet centre (metres, doubles);
  // sunDir: THREE.Vector3 unit; localCamPos: where the camera sits in the local scene.
  update(camPos, sunDir, localCamPos) {
    if (!this.hasAtmosphere) {
      if (!this.airless) { this.mesh.visible = false; return; }
      // Every ray misses the (absent) air, so the shader only paints the veil.
      const R = this.uniforms.uPlanetR.value;
      const r2 = camPos.x * camPos.x + camPos.y * camPos.y + camPos.z * camPos.z;
      const r = Math.sqrt(r2);
      this.uniforms.uCAtm.value = 1e30;
      this.uniforms.uSpace.value = 1;
      const fade = 1 - THREE.MathUtils.smoothstep(r - R, SKY_FADE_START, SKY_FADE_END);
      this.uniforms.uFade.value = fade;
      const sunElev = (sunDir.x * camPos.x + sunDir.y * camPos.y + sunDir.z * camPos.z) / r;
      const horizonDip = -Math.sqrt(Math.max(0, 1 - (R / r) * (R / r)));
      const sunlit = THREE.MathUtils.smoothstep(sunElev, horizonDip - 0.02, horizonDip + 0.12);
      this.uniforms.uStarDim.value = 0.93 * sunlit;
      this.mesh.visible = fade > 0.002;
      this.mesh.position.copy(localCamPos);
      return;
    }
    const R = this.uniforms.uPlanetR.value;
    const Ra = this.uniforms.uAtmR.value;
    const r2 = camPos.x * camPos.x + camPos.y * camPos.y + camPos.z * camPos.z;
    this.uniforms.uCamPos.value.set(camPos.x, camPos.y, camPos.z);
    this.uniforms.uCAtm.value = r2 - Ra * Ra;
    this.uniforms.uCGnd.value = r2 - R * R;
    this.uniforms.uSunDir.value.copy(sunDir);
    const alt = Math.sqrt(r2) - R;
    const top = Ra - R;
    this.uniforms.uSpace.value = THREE.MathUtils.smoothstep(alt, top * 0.6, top * 1.1);
    const fade = 1 - THREE.MathUtils.smoothstep(alt, SKY_FADE_START, SKY_FADE_END);
    this.uniforms.uFade.value = fade;
    // Star suppression follows how sunlit the scene is from the camera: full
    // in daylight above the air, none at night or deep in the atmosphere
    // (where the sky's own brightness already hides them).
    const r = Math.sqrt(r2);
    const sunElev = (sunDir.x * camPos.x + sunDir.y * camPos.y + sunDir.z * camPos.z) / r;
    const horizonDip = -Math.sqrt(Math.max(0, 1 - (R / r) * (R / r)));
    const sunlit = THREE.MathUtils.smoothstep(sunElev, horizonDip - 0.05, horizonDip + 0.1);
    this.uniforms.uStarDim.value = 0.65 * sunlit * this.uniforms.uSpace.value;
    this.mesh.visible = fade > 0.002;
    this.mesh.position.copy(localCamPos);
  }

  dispose() {
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}

// CPU-side approximation of the horizon sky colour, used for fog and the
// hemisphere light so ground objects sit in the same air as the dome.
export function approximateSkyColor(altitude, sunElevationSin, out = new THREE.Color(), bodyKey = 'earth') {
  const day = THREE.MathUtils.smoothstep(sunElevationSin, -0.10, 0.25);
  const dusk = 1 - THREE.MathUtils.smoothstep(Math.abs(sunElevationSin), 0.0, 0.25);
  if (bodyKey === 'mars') {
    // Butterscotch by day, greying toward the blue-white glow of a Martian
    // dusk; the dust reaches about 11 km scale height.
    out.setRGB(0.74, 0.55, 0.38).lerp(_dusk.setRGB(0.55, 0.58, 0.64), dusk * 0.6 * day);
    out.multiplyScalar(0.7 * day * Math.exp(-Math.max(0, altitude) / 11000));
    return out;
  }
  const zenith = _zenith.setRGB(0.20, 0.42, 0.85);
  out.setRGB(0.62, 0.72, 0.86).lerp(zenith, 0.35);
  out.lerp(_dusk.setRGB(0.85, 0.45, 0.25), dusk * 0.5 * day);
  const thin = Math.exp(-Math.max(0, altitude) / 9000);
  out.multiplyScalar(day * thin);
  return out;
}
const _zenith = new THREE.Color();
const _dusk = new THREE.Color();

// The sky low on the horizon: approximateSkyColor without its share of the
// zenith blue. Seen from high up, the haze over the far ground is a long
// grazing column of lit air, whose colour is this one, so the ground fades
// into the horizon sky instead of meeting it in a line.
export function approximateHorizonColor(sunElevationSin, out = new THREE.Color(), bodyKey = 'earth') {
  if (bodyKey !== 'earth') return approximateSkyColor(0, sunElevationSin, out, bodyKey);
  const day = THREE.MathUtils.smoothstep(sunElevationSin, -0.10, 0.25);
  const dusk = 1 - THREE.MathUtils.smoothstep(Math.abs(sunElevationSin), 0.0, 0.25);
  out.setRGB(0.66, 0.74, 0.86);
  out.lerp(_dusk.setRGB(0.92, 0.5, 0.26), dusk * 0.6 * day);
  out.multiplyScalar(day);
  return out;
}
